import { useParams } from 'react-router-dom';

import {
  InconsistencyLevel,
  InconsistencySource,
  InconsistencyType,
  getInconsistencySourceDisplayName,
  getInconsistencyTypeDisplayName,
} from '../../../../../backend/src/matter/inconsistency/inconsistency-display';
import { InconsistencyFilters } from '@/api/queries/useGetAllSubsetInconsistencies';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { DocumentFilter } from '@/new/components/document-filter';
import { ServerFilterMultiSelect } from '@/new/components/filter/server-filter-multi-select';
import { FilterPopoverContent } from '@/new/components/filter-popover-content';
import { Switch } from '@/new/components/shadcn/switch';
import { MyOptionType } from '@/types';

const typeOptions = Object.values(InconsistencyType);
const levelOptions = Object.values(InconsistencyLevel);
const sourceOptions = Object.values(InconsistencySource);

export function FilterPopover({
  filters,
  onFiltersChange,
  clearAllFilters,
  includedDocs,
  excludedDocs,
  onDocsChange,
}: {
  filters: InconsistencyFilters;
  onFiltersChange: (update: Partial<InconsistencyFilters>) => void;
  clearAllFilters: () => void;
  includedDocs: Map<string, MyOptionType>;
  excludedDocs: Map<string, MyOptionType>;
  onDocsChange: (docs: { includedDocs?: Map<string, MyOptionType>; excludedDocs?: Map<string, MyOptionType> }) => void;
}) {
  const { matterId } = useParams();
  if (!matterId) return null;

  return (
    <FilterPopoverContent clearAllFilters={clearAllFilters}>
      <label className="flex gap-4 text-gray-600">
        <Switch
          checked={filters.hasRelevantFacts ?? false}
          onCheckedChange={(checked) => onFiltersChange({ hasRelevantFacts: checked || undefined })}
        />
        <p>Contains Relevant Facts</p>
      </label>

      <div>
        <p className="mb-3 text-gray-600">Type</p>
        <ServerFilterMultiSelect
          values={filters.types ?? []}
          options={typeOptions}
          onChange={(values) => onFiltersChange({ types: values })}
          placeholder="Select inconsistency types..."
          formatLabel={getInconsistencyTypeDisplayName}
        />
      </div>

      <div>
        <p className="mb-3 text-gray-600">Level</p>
        <ServerFilterMultiSelect
          values={filters.levels ?? []}
          options={levelOptions}
          onChange={(values) => onFiltersChange({ levels: values })}
          placeholder="Select inconsistency levels..."
          formatLabel={capitalizeFirstLetter}
        />
      </div>

      <div>
        <p className="mb-3 text-gray-600">Source</p>
        <ServerFilterMultiSelect
          values={filters.sources ?? []}
          options={sourceOptions}
          onChange={(values) => onFiltersChange({ sources: values })}
          placeholder="Select source types..."
          formatLabel={getInconsistencySourceDisplayName}
        />
      </div>

      <div>
        <p className="mb-3 text-gray-600">Documents</p>
        <DocumentFilter
          includedDocs={includedDocs}
          excludedDocs={excludedDocs}
          handleDocsChange={onDocsChange}
          mode="include-facts-from"
        />
      </div>
    </FilterPopoverContent>
  );
}
